import {
  RadioButtonChecked as RadioButtonCheckedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import {
  Box,
  Fade,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { Question } from '../../types/questionnaire.types';

interface MatrixScaleQuestionProps {
  question: Question;
  response: string;
  onMatrixChange: (questionId: string, rowId: string, answerId: string) => void;
}

export const MatrixScaleQuestion: React.FC<MatrixScaleQuestionProps> = ({
  question,
  response,
  onMatrixChange,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const matrixAnswers = response ? JSON.parse(response || '{}') : {};

  // Ordenação numérica das colunas
  const sortedColumns = React.useMemo(() => {
    if (!question.matrixColumns) return [];

    return [...question.matrixColumns].sort((a, b) => {
      const numA = parseFloat(a.text);
      const numB = parseFloat(b.text);

      // Se ambos são números, ordena numericamente pelo texto
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numA - numB;
      }

      // Se apenas um é número, coloca o número primeiro
      if (!Number.isNaN(numA)) return -1;
      if (!Number.isNaN(numB)) return 1;

      // Se nenhum é número, tenta extrair número do ID
      // Exemplo: "ms_q6_col_1" -> 1
      const idNumRegex = /_(\d+)$/;
      const matchA = a.id.match(idNumRegex);
      const matchB = b.id.match(idNumRegex);

      if (matchA && matchB) {
        const idNumA = parseInt(matchA[1], 10);
        const idNumB = parseInt(matchB[1], 10);
        return idNumA - idNumB;
      }

      // Se não conseguir extrair número do ID, mantém ordem original
      // (não faz sort, retorna 0)
      return 0;
    });
  }, [question.matrixColumns]);

  if (!question.matrixRows || !question.matrixColumns) return null;

  // Renderização Desktop (Tabela)
  const renderDesktop = () => (
    <TableContainer
      sx={{
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'auto',
        maxWidth: '100%',
      }}
    >
      <Table sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <TableCell
              sx={{
                fontFamily: 'Roboto-Bold',
                fontSize: '9pt',
                color: '#ffffff',
                fontWeight: 'bold',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                minWidth: '250px',
                maxWidth: '350px',
                padding: '12px 8px',
                textAlign: 'center',
              }}
            >
              {/* AFIRMAÇÃO */}
            </TableCell>
            {sortedColumns.map((col) => (
              <TableCell
                key={col.id}
                align="center"
                sx={{
                  fontFamily: 'Roboto-Bold',
                  fontSize: '9pt',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px 4px',
                  minWidth: '50px',
                  width: '50px',
                  '&:last-child': {
                    borderRight: 'none',
                  },
                }}
              >
                {col.text}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {question.matrixRows?.map((row, rowIndex) => {
            const selectedAnswer = matrixAnswers[row.id];

            return (
              <TableRow
                key={row.id}
                sx={{
                  background: rowIndex % 2 === 0 ? '#ffffff' : '#f9fafb',
                  '&:hover': {
                    background: 'rgba(102, 126, 234, 0.05)',
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontFamily: 'Roboto-Regular',
                    fontSize: '9pt',
                    color: '#374151',
                    borderRight: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '12px 8px',
                  }}
                >
                  {row.text}
                </TableCell>
                {sortedColumns.map((col) => (
                  <TableCell
                    key={col.id}
                    align="center"
                    sx={{
                      borderRight: '1px solid #e5e7eb',
                      borderBottom: '1px solid #e5e7eb',
                      padding: '4px',
                      width: '50px',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                    }}
                  >
                    <Radio
                      checked={selectedAnswer === col.id}
                      onChange={() =>
                        onMatrixChange(question.id, row.id, col.id)
                      }
                      icon={
                        <RadioButtonUncheckedIcon
                          sx={{
                            color: '#d1d5db',
                            fontSize: '18px',
                            transition: 'all 0.2s ease',
                          }}
                        />
                      }
                      checkedIcon={
                        <RadioButtonCheckedIcon
                          sx={{
                            color: '#10b981',
                            fontSize: '18px',
                            filter:
                              'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))',
                            transition: 'all 0.2s ease',
                          }}
                        />
                      }
                      sx={{
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Renderização Mobile (Expandida)
  const renderMobile = () => (
    <Box sx={{ display: '100%' }}>
      {question.matrixRows?.map((row, rowIndex) => {
        const selectedAnswer = matrixAnswers[row.id];
        const isRowAnswered = !!selectedAnswer;

        return (
          <Fade in key={row.id} timeout={300 + rowIndex * 100}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: '12px',
                border: '1px solid #F0F0F0',
                borderColor: isRowAnswered ? '#667eea' : '#e5e7eb',
                background: isRowAnswered
                  ? 'rgba(102, 126, 234, 0.05)'
                  : '#ffffff',
                transition: 'all 0.3s ease',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto-Medium !important',
                  fontSize: '10.5pt !important',
                  color: '#374151 !important',
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                {row.text}
              </Typography>
              <FormControl
                component="fieldset"
                sx={{
                  width: '100%',
                  m: 0,
                  p: 0.5,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Roboto-Regular !important',
                    fontSize: '11pt !important',
                    color: '#374151 !important',
                    lineHeight: 1.5,
                  },
                }}
              >
                <RadioGroup
                  value={selectedAnswer || ''}
                  onChange={(e) =>
                    onMatrixChange(question.id, row.id, e.target.value)
                  }
                >
                  {sortedColumns.map((col) => (
                    <Box
                      key={col.id}
                      sx={{
                        mb: 1,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor:
                          selectedAnswer === col.id ? '#667eea' : '#e5e7eb',
                        background:
                          selectedAnswer === col.id
                            ? 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)'
                            : '#ffffff',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#667eea',
                          background: 'rgba(102, 126, 234, 0.03)',
                        },
                      }}
                    >
                      <FormControlLabel
                        value={col.id}
                        control={
                          <Radio
                            icon={
                              <RadioButtonUncheckedIcon
                                sx={{
                                  color: '#d1d5db',
                                  fontSize: '28px',
                                  transition: 'all 0.2s ease',
                                }}
                              />
                            }
                            checkedIcon={
                              <RadioButtonCheckedIcon
                                sx={{
                                  color: '#10b981',
                                  fontSize: '28px',
                                  filter:
                                    'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))',
                                  transition: 'all 0.2s ease',
                                }}
                              />
                            }
                          />
                        }
                        label={col.text}
                        sx={{
                          width: '100%',
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            fontFamily: 'Roboto-Regular !important',
                            fontSize: '11pt !important',
                            color: '#374151 !important',
                          },
                        }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>
              {question.required && !isRowAnswered && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    borderRadius: '6px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Roboto-Regular !important',
                      fontSize: '9pt !important',
                      color: '#ef4444 !important',
                      textAlign: 'center',
                    }}
                  >
                    Por favor, selecione uma opção
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        );
      })}
    </Box>
  );

  return isDesktop ? renderDesktop() : renderMobile();
};
